import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import styles from "./RelationshipsSection.module.css";
import templatePt from "../../data/pt/relationships_templates.json";
import templateEn from "../../data/en/relationships_templates.json";
import { renderText, renderArray } from "../../utils/templateRenderer";

/**
 * RelationshipsSection
 * Props:
 *  - premiumPayload
 *  - insights
 *  - derivedArchetype
 *  - userData
 *  - compact
 */
function fallbackRelationships({ temperament }) {
  const [E, A, S] = temperament || [50, 50, 50];
  return {
    intro: `Seu estilo relacional combina Emotionality=${E}%, Activity=${A}%, Sociability=${S}%.`,
    strengths: ["Capacidade de conexão", "Potencial empático"],
    challenges: ["Manter limites", "Sustentar intimidade emocional"],
    recommendations: [
      "Nomeie emoções antes de conversas difíceis",
      "Agende check-ins regulares",
    ],
    exercises: [
      {
        title: "Check-in",
        description: "Pergunte 'Como você está?' e escute 2 minutos.",
      },
    ],
    reflectionPrompts: ["O que me faz sentir seguro em relações?"],
    hints: [],
  };
}

export default function RelationshipsSection({
  premiumPayload = null,
  insights = null,
  derivedArchetype = null,
  userData = null,
  wrapPremium = null,
}) {
  const { i18n, t } = useTranslation("user");
  const template = i18n.language?.startsWith("pt") ? templatePt : templateEn;
  const archeKey = useMemo(() => {
    const candidate =
      (premiumPayload && premiumPayload.archetypeKey) ||
      (derivedArchetype && (derivedArchetype.key || derivedArchetype.label)) ||
      "default";
    return String(candidate).toLowerCase().replace(/\s+/g, "-");
  }, [premiumPayload, derivedArchetype]);

  const context = useMemo(
    () => ({
      userName:
        (userData && userData.name) ||
        (premiumPayload && premiumPayload.name) ||
        "Usuário",
      meta: (template[archeKey] && template[archeKey].meta) || {},
      personalityTraits: (premiumPayload &&
        premiumPayload.personalityTraits) || [50, 50, 50],
      characterTraits: (premiumPayload && premiumPayload.characterTraits) || {
        discipline: 50,
        mentalOpenness: 50,
        emotionalOpenness: 50,
        perspective: 50,
      },
      chosenCharacter: (premiumPayload && premiumPayload.chosenCharacter) || {},
      insights: insights || {},
    }),
    [premiumPayload, insights, derivedArchetype, userData, archeKey]
  );

  const tpl = template[archeKey] || template["default"] || null;

  const assembled = useMemo(() => {
    if (!tpl || !tpl.relationships) {
      return fallbackRelationships({
        temperament: context.personalityTraits.slice(0, 3),
      });
    }
    const r = tpl.relationships;
    const intro = renderText(r.intro || "", context);
    const strengths = renderArray(r.strengths || [], context);
    const challenges = renderArray(r.challenges || [], context);
    const recommendations = renderArray(r.recommendations || [], context);
    const exercises = Array.isArray(r.exercises)
      ? r.exercises.map((ex) => ({
          title: renderText(ex.title || "", context),
          description: renderText(ex.description || "", context),
        }))
      : [];
    const reflectionPrompts = renderArray(r.reflectionPrompts || [], context);
    const hints = renderArray(r.hints || [], context);
    return {
      intro,
      strengths,
      challenges,
      recommendations,
      exercises,
      reflectionPrompts,
      hints,
    };
  }, [tpl, context]);

  return (
    <section className={styles.relationshipsSection}>
      <header className={styles.header}>
        <h3>{t("profile.relationshipsTitle")}</h3>
      </header>
      <div className={styles.grid}>
        <div className={styles.col}>
          <h4>{t("profile.relationshipsSubtitle")}</h4>
          <p className={styles.lead}>{assembled.intro}</p>

          <h5>{t("profile.relationshipsPoints")}</h5>
          <ul className={styles.points}>
            {assembled.strengths.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
          <h5>{t("profile.relationshipsDangerPoints")}</h5>
          <ul className={styles.pointsDanger}>
            {assembled.challenges.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </div>

        <div className={styles.col}>
          <h5 style={{ marginTop: 12 }}>
            {t("profile.relationshipsRecommendations")}
          </h5>
          {wrapPremium(
            <ul className={styles.points}>
              {assembled.recommendations.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          )}

          <h5 style={{ marginTop: 12 }}>
            {t("profile.relationshipsExercises")}
          </h5>
          {wrapPremium(
            assembled.exercises && assembled.exercises.length > 0 && (
              <>
                <div className={styles.exList}>
                  {assembled.exercises.map((ex, i) => (
                    <div key={i} className={styles.exItem}>
                      <strong>{ex.title}</strong>
                      <div className={styles.exDesc}>{ex.description}</div>
                    </div>
                  ))}
                </div>
              </>
            )
          )}
        </div>
      </div>

      <div className={styles.bottom}>
        <div>
          <h5>{t("profile.growthReflection")}</h5>
          <ul className={styles.points}>
            {assembled.reflectionPrompts.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
        </div>

        {assembled.hints && assembled.hints.length > 0 && (
          <aside className={styles.sideHints}>
            <h5>{t("profile.growthHints")}</h5>
            <div className={styles.hints}>
              {assembled.hints.map((h, i) => (
                <span key={i} className={styles.hintBadge}>
                  {h}
                </span>
              ))}
            </div>
          </aside>
        )}
      </div>
    </section>
  );
}
